import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDialog, usePriceDropPredictionDetails } from "@apac/hooks";
import { alertKeySelector, isWatchingSelector } from "@apac/price-watch";
import {
  Dealness,
  type Prediction,
  type PriceDropMaximumRefund,
} from "@b2bportal/air-shopping-api";
import { FlightSearchSelectors, getPrediction } from "@b2bportal/core-flights";
import type { DialogProps } from "@b2bportal/core-ui";
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  AIR_PRICE_PREDICTION_RECOMMENDATION,
  VIEWED_FORECAST,
} from "@hopper-b2b/types";
import type { PriceForecastItem } from "@hopper-b2b/ui";
import { TripCategory } from "@overrides/types";
import { usePriceDisplay } from "@tenant/ui";
import type { WatchTripDialogProps } from "../../WatchTripDialog/WatchTripDialog";

export interface UsePricePredictionResponse {
  watchTripDialogProps: WatchTripDialogProps;
  priceDropDialogProps: DialogProps;
  learnMoreDialogProps: DialogProps;
  context: {
    items: Array<PriceForecastItem>;
    title: string;
    subtitle: string;
    lowestPrice: JSX.Element;
    prediction: Prediction;
    priceOnTripCategory: string;

    priceDropAmount?: PriceDropMaximumRefund;
    isPDPEligible: boolean;
    isWatching: boolean;
  };
}

export const usePricePrediction = (): UsePricePredictionResponse => {
  const { t } = useI18nContext();
  const prediction = useSelector(getPrediction);

  const flightKey = useSelector(alertKeySelector);
  const isWatching = useSelector(isWatchingSelector);

  const priceForeCastTitles = [
    { title: t("priceForecast.greatTitle"), dealness: Dealness.great },
    { title: t("priceForecast.goodTitle"), dealness: Dealness.good },
    { title: t("priceForecast.fairTitle"), dealness: Dealness.fair },
    { title: t("priceForecast.waitTitle"), dealness: Dealness.wait },
  ];

  const predictionTitleMap = {
    [Dealness.great]: t("priceForecast.greatDescription"),
    [Dealness.good]: t("priceForecast.goodDescription"),
    [Dealness.fair]: t("priceForecast.fairDescription"),
    [Dealness.wait]: t("priceForecast.waitDescription"),
  };

  const priceDropDialogProps = useDialog();
  const learnMoreDialogProps = useDialog();
  const watchTripDialogProps = {
    ...useDialog(),
    flightKey,
  };

  const { priceDropAmount, isPDPEligible } = usePriceDropPredictionDetails();

  useEffect(() => {
    if (prediction?.recommendationV2) {
      trackEvent({
        eventName: VIEWED_FORECAST,
        properties: {
          recommendation: prediction.recommendationV2,
        },
      });
      trackEvent({
        eventName: AIR_PRICE_PREDICTION_RECOMMENDATION,
        properties: {
          recommendation: prediction.recommendationV2,
          recommendation_before_confidence: prediction.recommendation,
          ...(prediction.trackingProperties?.properties || {}),
        },
      });
    }
  }, [prediction]);

  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);
  const priceOnTripCategory =
    tripCategory === TripCategory.ONE_WAY
      ? t("oneWayPerTraveler")
      : t("roundTripPerTraveler");

  const { combinedText: lowestPriceWithPoints } = usePriceDisplay({
    prices: prediction?.lowestPrice,
  });

  return {
    priceDropDialogProps,
    learnMoreDialogProps: {
      ...learnMoreDialogProps,
      open:
        !watchTripDialogProps.open &&
        !priceDropDialogProps.open &&
        learnMoreDialogProps.open,
    },
    watchTripDialogProps: {
      ...watchTripDialogProps,
      onSuccessClose: learnMoreDialogProps.onClose,
    },
    context: {
      prediction,
      items: priceForeCastTitles,
      lowestPrice: lowestPriceWithPoints,
      title: prediction?.predictionCopy?.recommendationTitle.join(" ") || "",
      subtitle: predictionTitleMap[prediction?.dealness] || "",
      isWatching,
      priceOnTripCategory,

      priceDropAmount,
      isPDPEligible,
    },
  };
};
